import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import { datasetQueryKeys } from "@/shared/domain/datasets/hooks";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { fileQueryKeys } from "@/shared/domain/files/hooks";
import { sessionQueryKeys } from "@/shared/domain/sessions/hooks";

import {
  CollectionRecord,
  CollectionResourceType,
  UpdateCollectionRequest,
} from "../CollectionRecord";

import { collectionQueryKeys } from "./collectionQueryKeys";

export interface UpdateCollectionVariables {
  collectionId: string;
  resourceOwnerId: string;
  updates: UpdateCollectionRequest;
}

/**
 * Query key of the resource list that embeds a collection's membership, keyed
 * by the type the collection holds.
 *
 * A `Record` over the union, so a fifth `CollectionResourceType` is a compile
 * error here rather than a type that silently invalidates nothing. A lookup
 * rather than a `switch` because `resource_type` arrives through an unchecked
 * response cast: an unrecognized value yields `undefined` instead of falling
 * off the end of an exhaustive switch.
 *
 * Scope these as narrowly as each domain's key factory allows. A key that is a
 * prefix of the collection-scoped one invalidates every sibling list too --
 * org-wide search, dataset tabs, other collections -- which is a regression
 * that reads as a passing test, since invalidating a prefix also marks the
 * scoped key stale. `dataset` and `session` are coarse only because their key
 * factories have no per-collection scope to reach for.
 */
const MEMBERSHIP_QUERY_KEYS: Record<
  CollectionResourceType,
  (record: CollectionRecord) => QueryKey
> = {
  dataset: () => datasetQueryKeys.lists(),
  file: (record) =>
    fileQueryKeys.listsScope(record.org_id, {
      type: "collection",
      id: record.collection_id,
    }),
  event: (record) =>
    eventQueryKeys.listsScope(record.org_id, {
      type: "collection",
      id: record.collection_id,
    }),
  session: () => sessionQueryKeys.lists(),
};

/**
 * The single write path for an existing collection -- metadata edits and
 * membership changes alike.
 *
 * The cache maintenance is deliberately baked in rather than exposed as a
 * helper callers invoke themselves: a write that lands without its
 * invalidation leaves the UI showing stale membership, and nothing about the
 * call site makes that omission visible. Everything that edits a collection
 * goes through here, including the AI client tools, which receive
 * `mutateAsync` rather than the raw service.
 */
export function useUpdateCollection() {
  const { collections } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<CollectionRecord, Error, UpdateCollectionVariables>({
    mutationFn: ({ collectionId, resourceOwnerId, updates }) =>
      collections.updateCollection(collectionId, updates, { resourceOwnerId }),
    onSuccess: (record) => {
      // The response is the newest state of the record, so it is written
      // straight into the detail cache rather than invalidated and refetched.
      queryClient.setQueryData(
        collectionQueryKeys.detail(record.collection_id),
        record,
      );
      void queryClient.invalidateQueries({
        queryKey: collectionQueryKeys.lists(record.org_id),
      });
      void queryClient.invalidateQueries({
        queryKey: collectionQueryKeys.changes(record.collection_id),
      });
      // Guarded because `invalidateQueries` with an undefined key skips key
      // matching altogether and invalidates every query in the cache.
      const membershipKey =
        MEMBERSHIP_QUERY_KEYS[record.resource_type]?.(record);
      if (membershipKey) {
        void queryClient.invalidateQueries({ queryKey: membershipKey });
      }
    },
  });
}
